import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import queryString from 'query-string';
import { isNil } from 'lodash';

import { checkCustomerStatus } from 'utils/clientUtils';
import {
  ServiceAgreementTypeEnum,
  CustomerInfoResponseModel,
  CustomerStatusEnum,
} from 'api/backofficeApiClient';
import {
  CustomerAdditionalInfoResponseModel,
  ClientStatus,
} from 'api/adminApiClient';
import {
  getClientAdditionalInfo,
  getClientInfo,
  getClientInfoByKey,
  getEditAdditionalInfoForCustomer,
  handleResetClientInfoByKey,
  setMemberAdditionalInfoVisible,
  updateCustomerClientStatus,
  updateCustomerIsEnterpriseClient,
} from 'redux/reducers/clientSlice';
import EditClient from './EditClient';
import { isURLWithId } from 'utils/urlUtils';
import { ClientDetailSection } from './enums';
import DownloadClient from './DownloadClient';
import useQueryMatch from 'hooks/useQueryMatch';
import ClientProgressBar from './ClientProgressBar';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import ClientMainInfoSection from './ClientMainInfoSection';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import DownloadClientAgreement from './DownloadClientAgreement';
import ClientAdditionalInfoSection from './ClientAdditionalInfoSection';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { isMemberAdditionalInfoVisibleSelector } from 'redux/selectors/clientSelectors';
import { hasMemberVisibleAdditionalInfo } from 'utils/additionalInfoVisibilityUtils';
import ActivitiesDetailsSection from 'components/ActivitiesDetailsSection';
import { ActivitiesSectionPageTypeEnum } from 'components/ActivitiesDetailsSection/enums';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import ClientStatusComponent from 'components/ClientStatus';
import EnterpriseClientCheckbox from 'components/EnterpriseClientCheckbox';

import styles from './ClientDetailsPage.module.less';
import { DownloadAgreementTypeEnum } from './DownloadClientAgreement/DownloadClientAgreement';
import { mapAgreementFilesLoadStatus } from './DownloadClientAgreement/agreementUtils';

const DEFAULT_SECTION = ClientDetailSection.PrimaryInfo;

type ParamsProps = {
  clientKey: string;
};

const getPageSections = (
  isAdditionalInfoVisible: boolean,
  isAdmin?: boolean,
) => {
  if (isAdmin || isAdditionalInfoVisible) {
    return Object.values(ClientDetailSection);
  }

  return [DEFAULT_SECTION];
};

export default function ClientDetailsPage() {
  const { clientKey } = useParams<ParamsProps>();
  const [clientDetails, setClientDetails] =
    useState<CustomerInfoResponseModel>();
  const [clientAdditionalDetails, setClientAdditionalDetails] =
    useState<CustomerAdditionalInfoResponseModel>();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const isAdminRole = useAppSelector(isAdminSelector);
  const isMemberAdditionalInfoVisible = useAppSelector(
    isMemberAdditionalInfoVisibleSelector,
  );
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const query = queryString.parse(location.search);
  const isAdditionalInfoVisible = isAdminRole
    ? !isNil(clientAdditionalDetails)
    : isMemberAdditionalInfoVisible === true;
  const pageSectionMatch = useQueryMatch(
    getPageSections(isAdditionalInfoVisible, isAdminRole),
  );
  const isId: boolean = isURLWithId(clientKey as string);

  useEffect(() => {
    // Need to reset the value of the client details in the store.
    // https://mybasepay.atlassian.net/browse/MBO-2713
    return () => {
      dispatch(handleResetClientInfoByKey());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!pageSectionMatch) {
      navigate(`${location.pathname}?section=${DEFAULT_SECTION}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch, isNil(clientAdditionalDetails), isAdditionalInfoVisible]);

  const onLoadClientAdditionalDetails = useCallback(
    async (clientId: string) => {
      const correctAction = isAdminRole
        ? getEditAdditionalInfoForCustomer
        : getClientAdditionalInfo;

      const result = await dispatch(correctAction(clientId));

      if (correctAction.fulfilled.match(result)) {
        setClientAdditionalDetails(result.payload);

        if (!isAdminRole) {
          dispatch(
            setMemberAdditionalInfoVisible(
              hasMemberVisibleAdditionalInfo(result.payload),
            ),
          );
        }
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch, isAdminRole],
  );

  const onLoadClientDetails = useCallback(async () => {
    const correctAction = isId ? getClientInfo : getClientInfoByKey;
    const correctData = isId
      ? { clientKey: clientKey as string, isAdminRole }
      : { clientKey: clientKey as string, isAdminRole };

    const result = await dispatch(correctAction(correctData));

    if (correctAction.fulfilled.match(result)) {
      (result.payload.isAdminAdditionalInfoExists || isAdminRole) &&
        (await onLoadClientAdditionalDetails(
          result.payload.customerId as string,
        ));

      setClientDetails(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [clientKey, isId, isAdminRole, dispatch, onLoadClientAdditionalDetails]);

  const loadClientDetails = useCallback(async () => {
    if (!clientKey) return;

    await onLoadClientDetails();

    setIsInitialLoaded(true);
  }, [clientKey, onLoadClientDetails]);

  useEffect(() => {
    if (!query.section) {
      navigate(
        `${location.pathname}?section=${ClientDetailSection.PrimaryInfo}`,
        { replace: true },
      );
    }
    // eslint-disable-next-line
  }, [query.section]);

  useEffect(() => {
    loadClientDetails();
  }, [loadClientDetails]);

  const hasAgreementAttached =
    (clientDetails?.agreementDetails.agreementDetailsType ===
      ServiceAgreementTypeEnum.ClientAgreement ||
      clientDetails?.agreementDetails.agreementDetailsType ===
        ServiceAgreementTypeEnum.MemberAgreement ||
      clientDetails?.agreementDetails.agreementDetailsType ===
        ServiceAgreementTypeEnum.MyBasePayAgreement) &&
    clientDetails?.agreementDetails.agreementAttachments?.length !== 0;

  const iAdditionalTabSelected =
    query.section === ClientDetailSection.AdditionalInfo;

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!clientDetails) return null;

  const { customerInfo, status, creator, clientStatus, isEnterpriseClient } =
    clientDetails;

  const { isDraft, isRequest, isDetails } = checkCustomerStatus(
    status as CustomerStatusEnum,
  );
  const isEditClient =
    isDraft ||
    (isAdminRole && (isRequest || isDetails)) ||
    (!isAdminRole && !iAdditionalTabSelected && (isRequest || isDetails));

  const getPageLabel = () => {
    if (isDraft) {
      return 'Draft customer details';
    }

    if (isRequest) {
      return 'Customer request details';
    }

    if (isDetails) {
      return 'Customer details';
    }

    return '';
  };

  const getInfoSection = () => {
    switch (query.section) {
      case ClientDetailSection.AdditionalInfo:
        return (
          <ClientAdditionalInfoSection
            clientDetails={
              clientAdditionalDetails as CustomerAdditionalInfoResponseModel
            }
          />
        );

      case ClientDetailSection.PrimaryInfo:
      default:
        return <ClientMainInfoSection clientDetails={clientDetails} />;
    }
  };

  const handleClientStatusChange = async (statusId: ClientStatus) => {
    if (!clientDetails?.customerId) return;

    const result = await dispatch(
      updateCustomerClientStatus({
        customerId: clientDetails.customerId,
        status: statusId,
      }),
    );

    if (updateCustomerClientStatus.fulfilled.match(result)) {
      await loadClientDetails();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleIsEnterpriseClientChange = async (value: boolean) => {
    if (!clientDetails?.customerId) return;

    const result = await dispatch(
      updateCustomerIsEnterpriseClient({
        customerId: clientDetails.customerId,
        isEnterpriseClient: value,
      }),
    );

    if (updateCustomerIsEnterpriseClient.fulfilled.match(result)) {
      await loadClientDetails();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const declinationReason =
    clientAdditionalDetails?.contractInformation?.contractRequirements?.declinationReason?.trim();
  const declinationReasonProvided = !!declinationReason;
  const declinedStatusDisabledReasons = declinationReasonProvided
    ? undefined
    : {
        [ClientStatus.Declined]:
          'Fill in Declination Reason in Additional Information → Contract Information to enable this status',
      };

  const pageLabel = getPageLabel();

  return (
    <div ref={containerRef} className="page-wrapper scroll-box">
      <div className="page-holder">
        {!isDraft && (
          <div className={styles.holder}>
            <ClientProgressBar
              client={clientDetails}
              onChange={setClientDetails}
            />
          </div>
        )}

        {pageLabel && (
          <BasePageLabel
            label={pageLabel}
            creator={creator}
            createdDate={clientDetails.createdDate}
          />
        )}

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <div className="page-head">
              <div className="page-head__left">
                <div className="page-title page-head__title">
                  {customerInfo.companyName}
                  {isAdminRole && (
                    <div>
                      <ClientStatusComponent
                        title="Customer status:"
                        clientStatus={clientStatus}
                        onStatusChange={handleClientStatusChange}
                        disabledStatusReasons={declinedStatusDisabledReasons}
                      />
                      <EnterpriseClientCheckbox
                        isEnterpriseClient={isEnterpriseClient}
                        onChange={handleIsEnterpriseClientChange}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="page-head__actions">
                {hasAgreementAttached && !iAdditionalTabSelected && (
                  <div>
                    <DownloadClientAgreement
                      listOfFiles={
                        clientDetails.agreementDetails.agreementAttachments
                      }
                      id={clientDetails.customerId}
                      agreementDetailsType={
                        clientDetails.agreementDetails.agreementDetailsType
                      }
                      downloadAgreementType={DownloadAgreementTypeEnum.Customer}
                    />
                  </div>
                )}

                {isEditClient && (
                  <div>
                    <EditClient
                      customerId={clientDetails?.customerId as string}
                      activeSection={query.section}
                      onSubmit={loadClientDetails}
                      isDraft={isDraft}
                      clientStatus={clientStatus}
                    />
                  </div>
                )}

                {isDetails && (
                  <div>
                    <DownloadClient
                      clientId={clientDetails.customerId}
                      hasCertificateOfInsurance={
                        !!clientAdditionalDetails?.contractInformation
                          ?.insuranceRequirements
                          ?.certificateOfInsuranceCreatedFileKey
                      }
                      listOfAgreementFiles={mapAgreementFilesLoadStatus(
                        clientDetails.agreementDetails.agreementAttachments,
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>{getInfoSection()}</div>
            <div className="page-box page-inner">
              {isAdminRole && (
                <>
                  <ActivitiesDetailsSection
                    entityId={clientDetails?.customerId as string}
                    pageType={ActivitiesSectionPageTypeEnum.Customer}
                    scrollRef={containerRef}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
